"use client";

import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { Bell as BellIcon } from "@phosphor-icons/react/dist/ssr/Bell";
import { List as ListIcon } from "@phosphor-icons/react/dist/ssr/List";

import type { NavItemConfig } from "../../../types/nav";
import { usePopover } from "../../../hooks/use-popover";

import { MobileNav } from "../mobile-nav";
import { NotificationsPopover } from "../notifications-popover";
import { UserPopover } from "../user-popover/user-popover";
import { PrimaryGroups, useGroupStore } from "../../../store/ProfileStore";
import { PartnerMobileNav } from "../partner-mobile-nav";
import { userProfileData } from "../../../Common/profileInfo";
import { decodeFromBase64, fetchQuery } from "../../../functions/misc";
import { GetProfile } from "../../../queries/getProfile";
import { GetPartner } from "../../../queries/GetPartner";

export interface MainNavProps {
  items: NavItemConfig[];
  user_type: string;
}

export function MainNav({
  items,
  user_type,
}: MainNavProps): React.ReactElement {
  const [openNav, setOpenNav] = React.useState<boolean>(false);

  return (
    <React.Fragment>
      <Box
        component="header"
        sx={{
          "--MainNav-background": "var(--mui-palette-background-default)",
          "--MainNav-divider": "var(--mui-palette-divider)",
          bgcolor: "var(--MainNav-background)",
          left: 0,
          position: "sticky",
          pt: { lg: "var(--Layout-gap)" },
          top: 0,
          width: "100%",
          zIndex: "var(--MainNav-zIndex)",
        }}
      >
        <Box
          sx={{
            borderBottom: "1px solid var(--MainNav-divider)",
            display: "flex",
            flex: "1 1 auto",
            minHeight: "var(--MainNav-height)",
            px: { xs: 2, lg: 3 },
            py: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "center", flex: "1 1 auto" }}
          >
            <IconButton
              onClick={(): void => {
                setOpenNav(true);
              }}
              sx={{ display: { lg: "none" } }}
            >
              <ListIcon />
            </IconButton>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
              flex: "1 1 auto",
              justifyContent: "flex-end",
            }}
          >
            {/*<NotificationsButton />*/}
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                borderColor: "var(--MainNav-divider)",
                display: { xs: "none", lg: "block" },
              }}
            />
            <UserButton />
          </Stack>
        </Box>
      </Box>
      {user_type === PrimaryGroups.Partner ? (
        <PartnerMobileNav
          items={items}
          onClose={() => {
            setOpenNav(false);
          }}
          open={openNav}
        />
      ) : (
        <MobileNav
          items={items}
          onClose={() => {
            setOpenNav(false);
          }}
          open={openNav}
        />
      )}
    </React.Fragment>
  );
}

function NotificationsButton(): React.ReactElement {
  const popover = usePopover<HTMLButtonElement>();

  return (
    <React.Fragment>
      <Tooltip title="Notifications">
        <Badge
          color="error"
          sx={{
            "& .MuiBadge-dot": {
              borderRadius: "50%",
              height: "10px",
              right: "6px",
              top: "6px",
              width: "10px",
            },
          }}
          variant="dot"
        >
          <IconButton onClick={popover.handleOpen} ref={popover.anchorRef}>
            <BellIcon />
          </IconButton>
        </Badge>
      </Tooltip>
      <NotificationsPopover
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
      />
    </React.Fragment>
  );
}

function UserButton(): React.ReactElement {
  const popover = usePopover<HTMLButtonElement>();
  const { primaryGroup } = useGroupStore();
  const [avatar, setAvatar] = React.useState<string>("");
  const fetchUserInfo = async () => {
    const profileId = await userProfileData();
    if(decodeFromBase64(primaryGroup) === PrimaryGroups.Partner){
      const profileInfo = await fetchQuery({ PartnerId: profileId }, GetPartner);
      setAvatar(
        `${profileInfo?.FirstName[0].toUpperCase()}${profileInfo?.LastName[0].toUpperCase()}`
      );
    }else {
      const profileInfo = await fetchQuery({ profileId: profileId }, GetProfile);
      setAvatar(
        `${profileInfo?.FirstName[0].toUpperCase()}${profileInfo?.LastName[0].toUpperCase()}`
      );
    }
  };

  React.useEffect(() => {
    fetchUserInfo();
  }, []);

  return (
    <React.Fragment>
      <Box
        component="button"
        onClick={popover.handleOpen}
        ref={popover.anchorRef}
        sx={{
          border: "none",
          background: "transparent",
          cursor: "pointer",
          p: 0,
        }}
      >
        <Badge
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          color="success"
          sx={{
            "& .MuiBadge-dot": {
              border: "2px solid var(--MainNav-background)",
              borderRadius: "50%",
              bottom: "6px",
              height: "12px",
              right: "6px",
              width: "12px",
            },
          }}
          variant="dot"
        >
          <Avatar> {avatar} </Avatar>
        </Badge>
      </Box>
      <UserPopover
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
      />
    </React.Fragment>
  );
}
